import React from "react";
import { useSelector } from "react-redux";
import { useI18n } from "i18n";
import { selectMyCompany } from "modules/company/redux/selectors";
import { Dialog } from "@remo-co/ui-core/src/components/Dialog";
import { DialogContent } from "@remo-co/ui-core/src/components/DialogContent";
import { IconButton } from "@remo-co/ui-core/src/components/IconButton";
import { Toolbar } from "@remo-co/ui-core/src/components/Toolbar";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import { Close } from "@remo-co/ui-core/src/icons/Close";
import { stringify } from "query-string";
// eslint-disable-next-line you-dont-need-lodash-underscore/is-nil
import { isNil, omitBy } from "lodash";
import { getVar } from "config";
import { selectUser } from "modules/auth/redux/selectors";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { ICompanyServicePlan } from "modules/company/types";
import { IEvent } from "modules/event/types";
import { ISubscriptionDetail } from "types/subscription";
import { getUserName } from "modules/userProfile";
import { addDialogNotification } from "modules/dialogNotification/redux/dialogNotificationSlice";
import { useAppDispatch } from "store/hooks";
import { usePrevious } from "../../helpers/reactHooksUtils";
import logger from "../../logging/logger";
import { sendGetRequest } from "../../services/apiService";
import useNotificationActions from "../notification/hooks/useNotificationActions";
import PlanDowngradePopup from "./components/planDowngrade/PlanDowngradePopup";

interface IUriType {
  type: "change" | "upgrade";
  plan_id?: string;
  subscription_id: string | null;
  customer_id: string | null;
  first_name: string;
  last_name: string;
  email: string | undefined;
  r: number;
}

interface Props {
  handleClose: () => void;
  setIsShowPlans: (isShow: boolean) => void;
  isShowPlans: boolean;
  subscriptionDetail: ISubscriptionDetail | null;
  isLoading: boolean;
}
const buildURI = (params: IUriType) => {
  const data = omitBy(params, isNil);
  const query = stringify(data);

  return `${getVar("REACT_APP_PRICING_PAGE_URL")}?${query}`;
};

const PlanChange = ({
  setIsShowPlans,
  handleClose,
  isShowPlans,
  subscriptionDetail,
  isLoading,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useI18n(["common", "settings", "server"]);
  const { myCompanyPlan } = useCompanyPlanSettings();
  const prevCompanyPlan = usePrevious(myCompanyPlan);
  const company = useSelector(selectMyCompany);
  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();
  const user = useSelector(selectUser);
  const localStorageKey = "plan_downgrade";
  const [firstName, lastName] = getUserName(user).split(" ");

  const subscriptionId =
    subscriptionDetail && subscriptionDetail.subscription
      ? subscriptionDetail.subscription.id
      : null;
  const plansURL = buildURI({
    type: subscriptionDetail ? "change" : "upgrade",
    plan_id: myCompanyPlan?.planId,
    subscription_id: subscriptionId,
    customer_id: subscriptionDetail?.customer?.id || null,
    first_name: firstName,
    last_name: lastName,
    email: user?.email,
    r: Math.random(),
  });

  React.useEffect(() => {
    // Registering only if my company plan is loaded, otherwise we are not able to get the plan details inside onMessage
    if (myCompanyPlan) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      window.addEventListener("message", onMessage);
    }

    if (prevCompanyPlan && myCompanyPlan) {
      logger.info(
        `[PlanChange] company plan changed for ${company?.id} from ${prevCompanyPlan.planId} to ${myCompanyPlan.planId}`,
      );

      if (prevCompanyPlan.level > myCompanyPlan.level) {
        logger.info(
          `[PlanChange] company plan downgraded for ${company?.id} from ${prevCompanyPlan.level} to ${myCompanyPlan.level}`,
        );
        const downgradedEvents = localStorage.getItem(localStorageKey);

        if (downgradedEvents) {
          logger.info("[PlanChange] Downgraded events.", {
            events: downgradedEvents,
          });
          localStorage.removeItem(localStorageKey);
          const data = JSON.parse(downgradedEvents);

          if (
            data.oldPlanId === prevCompanyPlan.planId &&
            data.newPlanId === myCompanyPlan.planId &&
            data.events &&
            data.events.length
          ) {
            logger.info("[PlanChange] Showing Downgraded events popup.", {
              events: downgradedEvents,
            });
            dispatch(
              addDialogNotification({
                hideCloseButton: true,
                hideDismissButton: true,
                message: "",
                content: (
                  <PlanDowngradePopup
                    events={data.events}
                    prevCompanyPlan={prevCompanyPlan}
                    myCompanyPlan={myCompanyPlan}
                  />
                ),
                title: t("settings:plan.downgrade"),
                confirmText: t("settings:ok.noted"),
              }),
            );
          }
        }
      }
    }

    return () => {
      // Remove iframe message handler - chargebee plan changes
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      window.removeEventListener("message", onMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myCompanyPlan, prevCompanyPlan]);

  /**
   * callback handler from iframe after completing chargbee checkout
   * @param ev
   */
  const onMessage = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (ev: any) => {
      const data = ev.data || "";

      if (typeof data === "string") {
        // If error
        if (data.indexOf("ADDON ERROR") === 0) {
          logger.error(
            `[PlanBilling][handleHostedPageLoad] Error during checking out: ${data}`,
          );
          addErrorNotification({ message: t("chargebee.addon.error") });
        } else if (data.indexOf("ERROR") === 0) {
          logger.error(
            `[PlanBilling][handleHostedPageLoad] Error checking out: ${data}`,
          );
          addErrorNotification({ message: t("try.again.later") });
        } else if (data === "SUCCESS") {
          addSuccessNotification({ message: t("settings:checkout.success") });
          // Close modal and fetch subscriptions
          handleClose();
        }
      } else if (data.type === "PLAN_SELECTED" && data.planId) {
        // This data will be sent from pricing page HTML to indicate user has selected a new plan (not checked out yet)
        // if user downgrades the plan, ie level < current level, then show a message
        logger.info(
          `[PlanChange][onMessage] getting new plan details: ${data.planId}`,
        );
        const { plan: newPlanDetails, message } = await sendGetRequest<
          APIResponse<{ plan: ICompanyServicePlan }>
        >(`/service-plan/${data.planId}`);

        if (newPlanDetails && myCompanyPlan) {
          if (newPlanDetails.level < myCompanyPlan.level) {
            logger.info(
              `[PlanChange][onMessage] user downgrading account from ${myCompanyPlan.planId} to ${newPlanDetails.planId}`,
            );
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            await handlePlanDowngrade(newPlanDetails, myCompanyPlan);
          }
        } else if (message) {
          addErrorNotification({ message: t(message) });
        }
      } else if (data.type === "OPEN_CHECKOUT" && data.planId) {
        logger.info(
          `[PlanBilling][handleHostedPageLoad] User has opened checkout for planId: ${data.planId}`,
        );
      } else if (data.type === "STEP" && data.planId && data.step) {
        logger.info(
          `[PlanBilling][handleHostedPageLoad] User has reached at step: ${data.step} for planId: ${data.planId}`,
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [myCompanyPlan],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePlanDowngrade = async (newPlan: any, oldPlan: any) => {
    if (!company) {
      logger.info("[PlanChange][handlePlanDowngrade] no company details.", {
        newPlan,
        oldPlan,
      });

      return;
    }
    const newPlanId = newPlan.planId;
    const oldPlanId = oldPlan.planId;
    const { events, message } = await sendGetRequest<
      APIResponse<{ events: IEvent[] }>
    >(`/company/${company.id}/downgraded-events?newPlan=${newPlanId}`);

    if (message) {
      addErrorNotification({ message: t(message) });
    }

    if (events && events.length) {
      logger.info(
        "[PlanChange][handlePlanDowngrade] Showing Downgraded events popup.",
        { events },
      );
      localStorage.setItem(
        localStorageKey,
        JSON.stringify({ newPlanId, oldPlanId, events }),
      );
      dispatch(
        addDialogNotification({
          hideCloseButton: true,
          message: "",
          content: (
            <PlanDowngradePopup
              events={events}
              beforeUpgrade
              prevCompanyPlan={oldPlan}
              myCompanyPlan={newPlan}
            />
          ),
          title: t("settings:downgrading.plan"),
          confirmText: t("settings:proceed.downgrade"),
          dismissText: t("settings:ok.cancel"),
          onDismiss: () => {
            setIsShowPlans(false);
          },
        }),
      );
    }
  };

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {isShowPlans && (
        <Dialog
          className="plans-dialog"
          open={isShowPlans}
          onClose={handleClose}
          maxWidth="xl"
        >
          <Toolbar>
            <div style={{ flexGrow: 1 }} />
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleClose}
              aria-label={t("modal.window.aria.label")}
              size="large"
            >
              <Close />
            </IconButton>
          </Toolbar>
          <DialogContent>
            {(isLoading && <PageLoader message={t("loading")} />) || (
              <iframe src={plansURL} title={t("settings:plan.change")} />
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default PlanChange;
